import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { t } from 'i18next'
import React, { useState } from 'react'
import { defaultPageSize, defaultRowsPerPageOptions } from '../../../config/defaultPagination'
import { RequestProps } from '../../../store/types'
import { Order } from '../../../store/types/components/table/order'
import LocalStorageTypes from '../../../store/types/enums/LocalStorageTypes'
import Cost from '../../../store/types/models/Cost'
import { StyledTableSortLabel } from '../../../styles/TableStyles'
import { StyledFooterCell, StyledHeaderCell } from '../../../styles/WorklogTableStyles'
import CostRow from './CostRow'

interface Column {
  id: string
  label: string
}

type CostTableProps = {
  setRequestProps: React.Dispatch<React.SetStateAction<RequestProps>>
  costs: Cost[]
  totalElements: number
  setCostToEdit: React.Dispatch<React.SetStateAction<Cost | null>>
  isLeader: boolean
  isMyCosts: boolean
  duplicateMode: boolean
  selected: Cost[]
  setSelected: React.Dispatch<React.SetStateAction<Cost[]>>
}

const CostTable = ({
  setRequestProps,
  costs,
  totalElements,
  setCostToEdit,
  isLeader,
  isMyCosts,
  duplicateMode,
  selected,
  setSelected,
}: CostTableProps) => {
  const [rowsPerPage, setRowsPerPage] = useState(
    parseInt(localStorage.getItem(LocalStorageTypes.CostTableRows) || defaultPageSize.toString())
  )
  const [page, setPage] = useState(0)
  const [orderProperty, setOrderProperty] = useState('')
  const [order, setOrder] = useState<Order>('asc')
  const totalCost = costs.reduce((sum, currentCost) => {
    return sum + currentCost.amount * currentCost.quantity
  }, 0)

  const columns: Column[] = [
    { id: 'principal', label: t('components.costSidebar.table.header.principal') },
    { id: 'type', label: t('components.costSidebar.table.header.type') },
    { id: 'amount', label: t('components.costSidebar.table.header.amount') },
  ]

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    setRequestProps(prev => ({ ...prev, page: newPage }))
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    localStorage.setItem(LocalStorageTypes.CostTableRows, String(+event.target.value))
    setPage(0)
    setRequestProps(prev => ({ ...prev, page: 0, size: +event.target.value }))
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderProperty === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderProperty(property)
    let orderNumber = 0
    if (order === 'asc') orderNumber = 0
    else orderNumber = 1
    setRequestProps(prev => ({ ...prev, sortOrder: orderNumber, sortField: orderProperty }))
  }

  return (
    <TableContainer sx={{ height: 'fit-content', border: '1px solid', borderColor: 'background.elevation8', flex: 1 }}>
      <Table size={'small'} sx={{ borderCollapse: 'separate' }}>
        <TableHead>
          <TableRow style={{ height: '50px' }}>
            <StyledHeaderCell component="td" />

            {columns.map(column => (
              <StyledHeaderCell
                key={column.id}
                align={'left'}
                sortDirection={orderProperty === column.id ? order : false}
              >
                <StyledTableSortLabel
                  active={orderProperty === column.id}
                  direction={orderProperty === column.id ? order : 'asc'}
                  onClick={e => handleRequestSort(e, column.id)}
                >
                  {column.label}
                  {orderProperty === column.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </StyledTableSortLabel>
              </StyledHeaderCell>
            ))}
            <StyledHeaderCell align="center" sx={{ position: 'sticky', right: 0 }}>
              {t('components.table.header.action')}
            </StyledHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {costs.map(cost => {
            return (
              <CostRow
                key={cost.id}
                cost={cost}
                setCostToEdit={setCostToEdit}
                isLeader={isLeader}
                isMyCosts={isMyCosts}
                duplicateMode={duplicateMode}
                selected={selected}
                setSelected={setSelected}
              />
            )
          })}
          <TableRow
            sx={{
              height: 50,
            }}
          >
            <TableCell />
            <TableCell colSpan={2}>
              <Typography sx={{ whiteSpace: 'break-spaces', fontWeight: 700 }}>
                {t('components.costSidebar.table.footer.sum')}
              </Typography>
            </TableCell>
            <TableCell colSpan={2}>
              <Typography fontWeight={700}>{`${totalCost.toFixed(2)} zł`}</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
        <TableFooter sx={{ bottom: 0, zIndex: 2, position: 'sticky' }}>
          <TableRow>
            <StyledFooterCell colSpan={columns.length + 2}>
              <label
                htmlFor={'cost-table-pagination'}
                id={'cost-table-pagination-label'}
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  margin: '-1px',
                  padding: 0,
                  overflow: 'hidden',
                  border: 0,
                  clip: 'rect(0, 0, 0, 0)',
                }}
              >
                {t('components.table.pagination.rowsPerPage')}
              </label>
              <TablePagination
                sx={{ position: 'sticky', right: 16, zIndex: 10, width: 'fit-content', float: 'right' }}
                component="div"
                rowsPerPageOptions={defaultRowsPerPageOptions}
                count={totalElements}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('components.table.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => {
                  return `${from} - ${to} ${t('components.table.pagination.of')} ${count}`
                }}
                slotProps={{
                  select: {
                    inputProps: { id: 'cost-table-pagination' },
                    'aria-labelledby': 'cost-table-pagination-label',
                  },
                }}
              />
            </StyledFooterCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}

export default CostTable
